import { handleSupabaseList, supabase } from "@/lib/supabase-unified";

export type Instructor = {
  name: string;
};

/**
 * Full instructor record for the Instructors browse page. `instrument` and
 * `description` exist on the table but are currently null for every row — the
 * UI simply hides them until they're populated.
 */
export type InstructorListItem = {
  id: number;
  name: string;
  slug: string;
  avatar_url_mobile: string | null;
  avatar_url_desktop: string | null;
  instrument: string | null;
  description: string | null;
};

const INSTRUCTOR_COLUMNS =
  "id, name, slug, avatar_url_mobile, avatar_url_desktop, instrument, description";

type InstructorRow = InstructorListItem & {
  instructor_courses?: { course_id: number }[] | null;
};

/**
 * All instructors, alphabetical. Small set (~13), so no pagination needed.
 * Filtered entirely in the query (nothing is dropped on the client):
 *  - `instructor_courses!inner` keeps only instructors that have ≥1 course.
 *  - the `.or(...)` keeps only instructors with at least one thumbnail;
 *    otherwise they'd render as a bare initial and look broken in the grid.
 */
export async function getAllInstructors(): Promise<InstructorListItem[]> {
  const rows = await handleSupabaseList<InstructorRow>(
    supabase
      .from("instructors")
      .select(`${INSTRUCTOR_COLUMNS}, instructor_courses!inner(course_id)`)
      .or("avatar_url_desktop.not.is.null,avatar_url_mobile.not.is.null")
      .not("slug", "is", null)
      .order("name", { ascending: true }) as any
  );
  return rows.map(({ instructor_courses: _ic, ...instructor }) => instructor);
}

/** Single instructor by slug (for the detail page). */
export async function getInstructorBySlug(
  slug: string
): Promise<InstructorListItem | null> {
  const rows = await handleSupabaseList<InstructorListItem>(
    supabase
      .from("instructors")
      .select(INSTRUCTOR_COLUMNS)
      .eq("slug", slug)
      .limit(1) as any
  );
  return rows[0] ?? null;
}

/**
 * Course ids taught by an instructor, in the same position/created_at order the
 * rest of the app uses. Callers hydrate these against the loaded catalog.
 */
export async function getCourseIdsByInstructorId(
  instructorId: number
): Promise<number[]> {
  const rows = await handleSupabaseList<{ course_id: number }>(
    supabase
      .from("instructor_courses")
      .select("course_id")
      .eq("instructor_id", instructorId)
      .order("position", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: true }) as any
  );
  return rows.map((r) => r.course_id);
}

export async function getInstructorsByCourseId(
  courseId: number
): Promise<Instructor[]> {
  const rows = await handleSupabaseList<{
    instructor: Instructor | null;
  }>(
    supabase
      .from("instructor_courses")
      .select("instructor:instructors(name)")
      .eq("course_id", courseId)
      .order("position", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: true }) as any
  );

  return rows
    .map((r) => r.instructor)
    .filter((v): v is Instructor => !!v);
}

